import classNames from "classnames";
import Cleave from "cleave.js/react";
import React, { CSSProperties, useEffect, useState } from "react";
import { isNodeEmpty } from "Tools";
import { ControlWrapper } from "./ControlWrapper";
import { SimpleFieldProps, useSimpleFieldProps } from "./FormTools";
import { ErrorMessage } from "./Tools";

export function EmailInput(props: {
  fieldProps: SimpleFieldProps<string | null>;
  placeholder?: string;
}) {
  return <Textbox {...props} type="email" />;
}

export interface TextboxProps {
  fieldProps: SimpleFieldProps<string | null>;
  placeholder?: string;
  className?: string;
  type?: string;
  isDisabled?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;

  style?: CSSProperties;
  leftIconContent?: React.ReactNode;
  rightIconContent?: React.ReactNode;

  cleaveOptions?: React.ComponentProps<typeof Cleave>["options"];

  setFocusedValue?: (value: string) => void;
}

export function Textbox(props: TextboxProps) {
  const field = useSimpleFieldProps(props.fieldProps);
  const [focusedValue, setFocusedValue] = useState("");
  useEffect(() => {
    props.setFocusedValue?.(focusedValue);
  }, [focusedValue]);
  const getValueFromElement = (e: HTMLInputElement & { rawValue?: string }) => {
    const result = e.rawValue ?? e.value;
    return result !== "" ? result : null;
  };
  return (
    <ControlWrapper
      style={props.style}
      leftIconContent={props.leftIconContent}
      rightIconContent={props.rightIconContent}
      errorMessage={<ErrorMessage>{field.error}</ErrorMessage>}
    >
      <CleaveOrHtmlInput
        {...props.inputProps}
        className={classNames("input", props.className, {
          "input-validation-error": field.error,
          "has-text-left": !isNodeEmpty(props.leftIconContent),
        })}
        id={field.name}
        name={field.name}
        disabled={props.isDisabled}
        placeholder={props.placeholder}
        type={props.type ?? "text"}
        value={focusedValue !== "" ? focusedValue : field.value ?? ""}
        onBlur={async e => {
          setFocusedValue("");
          await field.onBlur(getValueFromElement(e.target));
          props.inputProps &&
            props.inputProps.onBlur &&
            props.inputProps.onBlur(e);
        }}
        onChange={async e => {
          setFocusedValue(e.target.value);
          await field.onChange(getValueFromElement(e.target));
          props.inputProps &&
            props.inputProps.onChange &&
            props.inputProps.onChange(e);
        }}
        onFocus={async e => {
          setFocusedValue(e.target.value);
          await field.onFocus(getValueFromElement(e.target));
        }}
        cleaveOptions={props.cleaveOptions}
      />
    </ControlWrapper>
  );
}

function CleaveOrHtmlInput({
  cleaveOptions,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  cleaveOptions?: React.ComponentProps<typeof Cleave>["options"];
}) {
  return cleaveOptions ? (
    <Cleave {...props as any} options={cleaveOptions} />
  ) : (
    <input {...props} />
  );
}
